<template>
  <div
    class="bg-white rounded-[12px] p-5 shadow-sm border border-[#E5E5E5]"
    style="border-left: 4px solid #EAB308"
    data-testid="impact-forecast-block"
  >
    <div class="flex items-center gap-2 mb-3">
      <i class="pi pi-chart-line text-[0.9rem]" style="color: #EAB308" aria-hidden="true" />
      <h3 class="text-[13px] uppercase tracking-wider font-medium text-[#ABABAB] m-0">
        {{ $t('UserProjects.impactAnalysis.forecast.title') }}
      </h3>
      <span
        class="text-[10px] font-medium px-2 py-0.5 rounded-full"
        style="background-color: #FEFCE8; color: #EAB308"
      >
        {{ $t('UserProjects.impactAnalysis.forecast.badge') }}
      </span>
    </div>

    <div v-if="!forecast || forecast.insufficientData" class="text-[13px] text-[var(--color-disabled)]">
      {{ $t('UserProjects.impactAnalysis.forecast.insufficient') }}
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div>
        <p class="text-[10px] uppercase tracking-wider text-[var(--color-disabled)] mb-1 m-0">
          {{ $t('UserProjects.impactAnalysis.forecast.milestoneRisk') }}
        </p>
        <div class="flex items-center gap-1.5">
          <i
            v-if="forecast.riskLevel === 'red' || forecast.riskLevel === 'yellow'"
            class="pi pi-exclamation-triangle text-[0.9rem]"
            :style="{ color: forecast.riskLevel === 'red' ? '#EF4444' : '#EAB308' }"
            aria-hidden="true"
          />
          <p class="text-[15px] font-bold text-[var(--color-dark)] m-0">
            {{ forecast.criticalMilestone?.title || '—' }}
          </p>
        </div>
        <p
          class="text-[12px] mt-0.5 mb-0"
          :style="{ color: riskColor }"
        >
          {{ riskLabel }}
        </p>
      </div>
      <div>
        <p class="text-[10px] uppercase tracking-wider text-[var(--color-disabled)] mb-1 m-0">
          {{ $t('UserProjects.impactAnalysis.forecast.projectedDate') }}
        </p>
        <p class="text-[15px] font-bold text-[var(--color-dark)] m-0">
          {{ projectedDateLabel }}
        </p>
        <p class="text-[12px] text-[var(--color-disabled)] mt-0.5 mb-0">
          {{ driftLabel }}
        </p>
      </div>
      <div>
        <p class="text-[10px] uppercase tracking-wider text-[var(--color-disabled)] mb-1 m-0">
          {{ $t('UserProjects.impactAnalysis.forecast.changeVsPrev') }}
        </p>
        <p class="text-[15px] font-bold m-0" :style="{ color: trendColor }">
          {{ trendLabel }}
        </p>
      </div>
    </div>

    <div class="flex items-start gap-1.5 mt-3 pt-3 border-t border-[#F0F0F0] text-[11px] text-[var(--color-disabled)]">
      <i class="pi pi-info-circle text-[0.75rem] mt-0.5 shrink-0" aria-hidden="true" />
      <span>{{ $t('UserProjects.impactAnalysis.forecast.disclaimer') }}</span>
    </div>
  </div>
</template>

<script>
import { formatShortDate } from '@/utils/impactAnalysisDisplay';

export default {
  name: 'ImpactForecastBlock',
  props: {
    forecast: { type: Object, default: null },
  },
  computed: {
    riskColor() {
      if (this.forecast?.riskLevel === 'red') return '#EF4444';
      if (this.forecast?.riskLevel === 'yellow') return '#EAB308';
      return '#22C55E';
    },
    riskLabel() {
      const level = this.forecast?.riskLevel;
      if (level === 'red') return this.$t('UserProjects.impactAnalysis.forecast.riskHigh');
      if (level === 'yellow') return this.$t('UserProjects.impactAnalysis.forecast.riskElevated');
      return this.$t('UserProjects.impactAnalysis.forecast.riskStable');
    },
    projectedDateLabel() {
      return formatShortDate(this.forecast?.criticalMilestone?.projectedCompletionDate)
        || formatShortDate(this.forecast?.criticalMilestone?.dueDate);
    },
    driftLabel() {
      const days = this.forecast?.criticalMilestone?.projectedDriftDays;
      if (days == null) return '';
      return this.$t('UserProjects.impactAnalysis.forecast.driftDays', { days });
    },
    trendColor() {
      const dir = this.forecast?.riskTrendDirection;
      if (dir === 'falling' || dir === 'down') return '#EF4444';
      if (dir === 'rising' || dir === 'up') return '#22C55E';
      return '#6B6B6B';
    },
    trendLabel() {
      const dir = this.forecast?.riskTrendDirection;
      if (dir === 'falling' || dir === 'down') {
        return this.$t('UserProjects.impactAnalysis.forecast.worsening');
      }
      if (dir === 'rising' || dir === 'up') {
        return this.$t('UserProjects.impactAnalysis.forecast.improving');
      }
      return this.$t('UserProjects.impactAnalysis.forecast.stable');
    },
  },
};
</script>
